/**
 * The symbol lock on a chart's toolbar — 🔒 before the symbol when it is locked,
 * 🔓 there on hover when it is not (owner, 2026-10-06; `docs/plans/symbol-lock.md`).
 *
 * ```
 * locked      ☆ 🔒 DIA        click 🔒 → unlocks this chart
 * unlocked    ☆ DIA           hovered: ☆ 🔓 DIA, click → locks it
 * ```
 *
 * ## Inside the symbol box, like the star
 *
 * The toolbar row is counted (see `ChartListStar`): the symbol is centred by a
 * mirror spacer, so a key beside the box would push it off centre. The lock
 * therefore rides inside the box's left padding, between the star and the label,
 * and `ChartToolbar` puts the same `LOCK_ROOM` on the right and takes both off
 * the width — the box's outer size, and with it the row, do not move. The cost
 * is paid by the label, which ellipsises 28px sooner.
 *
 * **The room is reserved whether or not the lock shows.** The 🔓 appears on
 * hover (decision 3, so locking is discoverable), and making room only then
 * would move the label under the pointer that is hovering it — the reflow
 * `SymbolSearch` is careful never to cause. So the room is always there, and
 * hover changes opacity, never geometry.
 *
 * Measured against the label, not the box edge, with `ChartListStar`'s own
 * measure (`labelStart`), so the two cannot disagree about where the text is.
 *
 * ## The toast
 *
 * `LockNotice` is the "DIA is locked · 🔓 Unlock" that answers a refused symbol
 * change. It floats under the box with `SNAP_NOTE`'s look — the toolbar's one
 * existing transient note — and for its reason: a message that reflowed the
 * fixed strip would move the symbol out from under the pointer that caused it.
 */

import { useLayoutEffect, useRef, useState } from "react";

import { labelStart } from "./ChartListStar";
import { useSymbolName } from "../lib/symbol-index";

/** Room the lock takes inside the symbol box's padding, each side, in px. */
export const LOCK_ROOM = 14;

/** Between the lock and the first letter of the label, in px. */
const LOCK_GAP = 2;

export interface ChartSymbolLockProps {
  symbol: string;
  locked: boolean;
  /** Whether the pointer is over the symbol — the only time 🔓 shows. */
  hovered: boolean;
  /** Room the star holds to the lock's left, so the lock never sits on it. */
  starRoom: number;
  onToggle: (locked: boolean) => void;
}

export function ChartSymbolLock({ symbol, locked, hovered, starRoom, onToggle }: ChartSymbolLockProps) {
  const button = useRef<HTMLButtonElement>(null);
  const [left, setLeft] = useState(2 + starRoom);
  const [focused, setFocused] = useState(false);
  // The label is `symbolLabel(symbol, name)`; the name lands after first paint.
  const name = useSymbolName(symbol);

  useLayoutEffect(() => {
    const input = button.current?.parentElement?.querySelector("input");
    if (!input) return;
    const place = () =>
      setLeft(Math.max(input.offsetLeft + 2 + starRoom, labelStart(input) - LOCK_GAP - LOCK_ROOM));
    place();
    let live = true;
    void document.fonts?.ready.then(() => live && place());
    return () => {
      live = false;
    };
  }, [symbol, name, starRoom]);

  const shown = locked || hovered || focused;
  return (
    <button
      ref={button}
      type="button"
      aria-label={locked ? `Unlock ${symbol}` : `Lock ${symbol}`}
      aria-pressed={locked}
      title={
        locked
          ? `${symbol} is locked — a screener click or typing will not replace it. Click to unlock.`
          : `Lock ${symbol} — keep this chart on it until you unlock`
      }
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onClick={() => onToggle(!locked)}
      style={{
        ...LOCK,
        left,
        // Hidden by opacity, not removed: it stays in the tab order, and the
        // reserved room means showing it moves nothing.
        opacity: shown ? 1 : 0,
        color: locked ? "var(--qf-text)" : "var(--qf-text-secondary)",
      }}
    >
      {locked ? "🔒" : "🔓"}
    </button>
  );
}

/** "DIA is locked · 🔓 Unlock", under the symbol box. */
export function LockNotice({ symbol, onUnlock }: { symbol: string; onUnlock: () => void }) {
  return (
    <span role="status" style={NOTICE}>
      {`${symbol} is locked`}
      <span aria-hidden style={{ color: "var(--qf-text-muted)" }}>
        ·
      </span>
      <button type="button" onClick={onUnlock} style={UNLOCK}>
        🔓 Unlock
      </button>
    </span>
  );
}

const LOCK: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: LOCK_ROOM,
  padding: 0,
  background: "transparent",
  border: "none",
  font: "inherit",
  fontSize: "0.75rem",
  lineHeight: 1,
  cursor: "pointer",
  transition: "opacity 120ms",
};

/** `ChartToolbar`'s `SNAP_NOTE`, centred under the box rather than left-aligned. */
const NOTICE: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: "50%",
  transform: "translateX(-50%)",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  whiteSpace: "nowrap",
  fontSize: 11,
  padding: "3px 7px",
  borderRadius: 4,
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  color: "var(--qf-text-secondary)",
  zIndex: 20,
};

const UNLOCK: React.CSSProperties = {
  padding: "1px 6px",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-accent)",
  font: "inherit",
  cursor: "pointer",
};
